import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import endpoints from '../../../../../../api/endpoints';
import { useFetch } from '../../../../../../api/useFetch';
import CommonReports from '../../../../../common/reports';
import { downloadReport } from '../../../../../common/reports/utils/downloadReport';
import { HandleDownloadReport } from '../../../../../common/reports/actions/interfaces';
import { getPermissionToGenerateReports } from '../../../../../common/reports/utils/mapReportToTableStructure';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import { FiltersFormValues, FiltersInitialData, ReportsData } from '../../../../../common/reports/interfaces';

import locales from './locales';
import FilterDetails from './details/summary';
import FiltersSummary from './details/filters';
import { mapTableDataIntoApiFormat } from './dataTransformator';
import FiltersForm from './create-report-filter-form/form';
import { validationSchema } from './create-report-filter-form/validationSchema';
import { AccountTransfersReportCreateForm, AccountTransfersReportDetailsMetadataResponse } from './interfaces';

const initFilters = {
  name: '',
  operationIds: [],
  from: null,
  to: null,
  statuses: [],
  sendersMsisdns: []
};

const AccountTransfersReport = () => {
  const location = useLocation<{ filters: FiltersInitialData } | undefined>();
  const [initialValues, setInitialValues] = useState(location.state?.filters || initFilters);
  const { table, ...commonReports } = locales;
  const { data = {} as ReportsData, loading, error, refresh } = useFetch<ReportsData>({
    pathname: endpoints.accountTransfersReports
  });

  const handleDeleteReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.accountTransfersReports}/${id}`, {
      method: 'delete'
    });

  const handleCancelReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.accountTransfersReports}/${id}`, {
      method: 'delete'
    });

  const handleRetryReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.accountTransfersReportsRetry(id)}`, {
      method: 'POST'
    });

  const handleRegenerateReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.accountTransfersReportsRegenerate(id)}`, {
      method: 'POST'
    });

  const handleGenerateReportRequest = (values: AccountTransfersReportCreateForm) =>
    fetchWithTokenRefresh(endpoints.accountTransfersReports, addParams('POST', mapTableDataIntoApiFormat(values)));

  const handleDownloadReport: HandleDownloadReport = (id, name, size) => {
    downloadReport(id, `${name}.csv`, 'transfer', size);
  };

  return (
    <CommonReports
      error={error}
      filters={{
        form: (filtersFormValues: FiltersFormValues<AccountTransfersReportCreateForm>) => (
          <FiltersForm {...filtersFormValues} />
        ),
        validation: validationSchema,
        details: (metadata: AccountTransfersReportDetailsMetadataResponse) => <FilterDetails filters={metadata} />,
        summary: (metadata: AccountTransfersReportDetailsMetadataResponse) => <FiltersSummary filters={metadata} />,
        initialValues,
        clearValues: () => {
          setInitialValues(initFilters);
        },
        predefinedReportGeneration: Boolean(location.state?.filters)
      }}
      locales={commonReports}
      data={data}
      emptyMsg={table.emptyMsg}
      loading={loading}
      refresh={refresh}
      handleDeleteReportRequest={handleDeleteReportRequest}
      handleCancelReportRequest={handleCancelReportRequest}
      handleRetryReportRequest={handleRetryReportRequest}
      handleRegenerateReportRequest={handleRegenerateReportRequest}
      handleGenerateReportRequest={handleGenerateReportRequest}
      handleDownloadReport={handleDownloadReport}
      generationAllowed={getPermissionToGenerateReports(data.limits?.status)}
    />
  );
};

export default AccountTransfersReport;
